import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleUser,
  faHome,
  faUsersGear,
  faEraser,
  faHandHoldingHeart,
  faUserTag,
  faChevronRight,
  faChevronLeft,
  faCog,
  faUserCheck,
  faSchool,
  faHandshake,
  faCheckSquare,
  faHandHoldingDollar,
  faFileInvoice,
  faFilter,
  faBuilding,
  faUserCircle,
  faPiggyBank,
  faShareAlt,
  faCheckDouble,
  faArchive,
  faUserClock,
  faUsers,
  faBriefcase,
  faMoneyBillWave,
  faChevronDown,
  faLayerGroup,
  faUserGear,
  faShieldHalved,
  faUserAstronaut,
  faWrench,
  faBook,
  faBank
} from '@fortawesome/free-solid-svg-icons';
import { FaTripadvisor } from 'react-icons/fa';
import { faReadme } from '@fortawesome/free-brands-svg-icons';

// ================================================================
// CONFIGURATION DES SECTIONS (avec filtrage par rôle)
// ================================================================
const getSectionConfig = (fonction) => {
  // Définition des chemins à masquer pour certains rôles
  const restrictedPaths = fonction === 'DSI' ? ['/admins', '/Erase'] : [];

  const baseSections = [
    {
      id: 'main',
      label: 'Principal',
      icon: faHome,
      items: [
        { to: '/Home', icon: faHome, label: 'Vue d\'ensemble' }
      ]
    },
    {
      id: 'activities',
      label: 'Activités',
      icon: faBriefcase,
      items: [
        { to: '/offres', icon: faHandHoldingHeart, label: 'Offres d\'emplois' },
        { to: '/candidats', icon: faUserCheck, label: 'Candidats' },
        { to: '/formations', icon: faSchool, label: 'Formations' },
        { to: '/clients', icon: faUserCircle, label: 'Gestion des clients' },
        { to: '/inscription', icon: faCheckDouble, label: 'Inscri Formation' },
        { to: '/placement', icon: faShareAlt, label: 'Placement' }
      ]
    },
    {
      id: 'partners',
      label: 'Partenaires',
      icon: faHandshake,
      items: [
        { to: '/fournisseurs', icon: faBuilding, label: 'Liste de fournisseurs' },
        { to: '/partenaires', icon: faBuilding, label: 'Liste de Entreprises' }
      ]
    },
    {
      id: 'hr',
      label: 'Ressource Humaine',
      icon: faUsers,
      items: [
        { to: '/employes', icon: faUserTag, label: 'Employés' },
        { to: '/presences', icon: faCheckSquare, label: 'Présences' },
        { to: '/evaluationEmploye', icon: faCheckDouble, label: 'Évaluation' },
        { to: '/conges', icon: faUserClock, label: 'Gestion des congés' },
        { to: '/supervision', icon: faUserAstronaut, label: 'Mission & Supervision' }
      ]
    },
    {
      id: 'finance',
      label: 'Comptabilité & Caisse',
      icon: faMoneyBillWave,
      items: [
        { to: '/depenses', icon: faFilter, label: 'Caisse & Dépenses' },
        { to: '/caisseBanque', icon: faPiggyBank, label: 'Caisse & Bank' },
        { to: '/paiement', icon: faHandHoldingDollar, label: 'Paiements agents' },
        { to: '/factures', icon: faFileInvoice, label: 'Ventes Formations' },
        { to: '/factures_placement', icon: faFileInvoice, label: 'Factures Placements' },
        { to: '/parametres-paie', icon: faWrench, label: 'Parametre de paiement' }
      ]
    },
    {
      id: 'settings',
      label: 'Administration',
      icon: faShieldHalved,
      items: [
        { to: '/archivage', icon: faArchive, label: 'Archivages Numérique' },
        { to: '/manage-critaire', icon: faWrench, label: 'Manage Critaire Evaluation' },
        { to: '/entreprise', icon: faBuilding, label: 'Entreprise Infos' },
        { to: '/admins', icon: faUsersGear, label: 'Administrateurs' },
        { to: '/Erase', icon: faEraser, label: 'Réinitialisation' }
      ]
    },
    {
      id: 'tuto',
      label: 'Tutoriels',
      icon: faReadme,
      items: [
        { to: '/presentation', icon: faBook, label: 'Presentation' },
        { to: '/Administration', icon: faReadme, label: 'Administration' },
        { to: '/TutoCaisse', icon: faReadme, label: 'Comptabilité & Caisse' },
        { to: '/TutoRH', icon: faReadme, label: 'Ressource Humaine' },
        { to: '/TutoFormation', icon: faReadme, label: 'Formation' }
      ]
    }
  ];

  // Filtrer les sections selon les restrictions
  return baseSections.map(section => {
    if (section.id === 'settings' && restrictedPaths.length > 0) {
      return {
        ...section,
        items: section.items.filter(item => !restrictedPaths.includes(item.to))
      };
    }
    return section;
  });
};

// ================================================================
// COMPOSANT PRINCIPAL
// ================================================================
const Sidebar = ({ userId, fonction, onLinkClick, logo, nom }) => {
  const location = useLocation();
  const sections = useMemo(() => getSectionConfig(fonction), [fonction]);

  // --- État rétracté ---
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed_state');
    return saved ? JSON.parse(saved) : false;
  });

  // --- État sections ouvertes ---
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('sidebar_expanded_sections');
    if (saved) {
      try { return JSON.parse(saved); }
      catch { /* fallthrough */ }
    }
    const defaultOpen = {};
    sections.forEach(s => { defaultOpen[s.id] = true; });
    return defaultOpen;
  });

  // --- Auto-ouvrir la section du lien actif ---
  useEffect(() => {
    const activeSection = sections.find(section =>
      section.items.some(item => location.pathname.startsWith(item.to))
    );
    if (activeSection && !expandedSections[activeSection.id]) {
      setExpandedSections(prev => ({ ...prev, [activeSection.id]: true }));
    }
  }, [location.pathname, sections]);

  // --- Persistance ---
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed_state', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('sidebar_expanded_sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  // --- Handlers ---
  const toggleSection = useCallback((sectionId) => {
    if (isCollapsed) return;
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, [isCollapsed]);

  const handleLinkClick = () => {
    if (onLinkClick) onLinkClick();
  };

  const isSectionActive = (items) => {
    return items.some(item => location.pathname.startsWith(item.to));
  };

  const userInitial = (fonction || 'U').charAt(0).toUpperCase();

  // --- Rendu ---
  return (
    <>
      <style>{`
        /* ================================================================
           SIDEBAR ULTRA PREMIUM – GLASSMORPHISM LUXE (DESIGN AMÉLIORÉ)
           ================================================================ */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        :root {
          --glass-bg: rgba(8, 15, 35, 0.78);
          --glass-border: rgba(59, 130, 246, 0.12);
          --glass-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
          --primary-blue: #3b82f6;
          --accent-cyan: #38bdf8;
          --primary-glow: rgba(59, 130, 246, 0.35);
          --text-light: #e8edff;
          --text-muted: #94a3b8;
          --radius-lg: 28px;
          --radius-md: 16px;
          --transition: cubic-bezier(0.22, 1, 0.36, 1);
        }

        /* --- Conteneur --- */
        .glass-sidebar-content {
          height: 100vh;
          width: 280px;
          display: flex;
          flex-direction: column;
          background: var(--glass-bg);
          backdrop-filter: blur(28px) saturate(1.5);
          -webkit-backdrop-filter: blur(28px) saturate(1.5);
          border-right: 1px solid var(--glass-border);
          border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
          box-shadow: var(--glass-shadow), inset 0 1px 0 rgba(255,255,255,0.04);
          transition: width 0.5s var(--transition), box-shadow 0.3s ease;
          overflow: visible;
          position: relative;
          z-index: 1040;
          color: var(--text-light);
          font-family: 'Inter', -apple-system, sans-serif;
        }
        .glass-sidebar-content:hover {
          box-shadow: var(--glass-shadow), 0 0 40px rgba(59,130,246,0.05);
        }
        .glass-sidebar-content.collapsed {
          width: 80px;
        }

        /* --- Toggle --- */
        .toggle-handle {
          position: absolute;
          right: -18px;
          top: 50%;
          transform: translateY(-50%);
          width: 38px;
          height: 38px;
          background: rgba(10, 20, 50, 0.85);
          backdrop-filter: blur(14px);
          border: 1.5px solid var(--glass-border);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-blue);
          cursor: pointer;
          transition: all 0.4s var(--transition);
          box-shadow: 0 6px 24px rgba(0,0,0,0.3);
          z-index: 10;
        }
        .toggle-handle:hover {
          color: white;
          background: var(--primary-blue);
          border-color: var(--primary-blue);
          transform: translateY(-50%) scale(1.08);
          box-shadow: 0 0 30px var(--primary-glow);
        }
        .toggle-handle:active { transform: translateY(-50%) scale(0.92); }

        /* --- Header --- */
        .sidebar-header {
          flex-shrink: 0;
          padding: 28px 20px 20px 20px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 14px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          min-height: 80px;
        }
        .glass-sidebar-content.collapsed .sidebar-header {
          justify-content: center;
          padding: 28px 0 20px 0;
        }
        .app-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }
        .brand-icon-wrapper {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          background: linear-gradient(135deg, var(--primary-blue), var(--accent-cyan));
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.3rem;
          box-shadow: 0 6px 20px var(--primary-glow);
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        .brand-icon-wrapper:hover {
          transform: scale(1.05) rotate(-2deg);
        }
        .brand-text {
          font-weight: 800;
          font-size: 1.2rem;
          letter-spacing: -0.5px;
          color: white;
          white-space: nowrap;
        }
        .brand-highlight {
          background: linear-gradient(135deg, #60a5fa, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .brand-sub {
          font-size: 0.6rem;
          font-weight: 600;
          color: var(--text-muted);
          letter-spacing: 2.5px;
          text-transform: uppercase;
          opacity: 0.5;
          margin-top: 2px;
        }

        /* --- Scroll --- */
        .sidebar-scroll {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 16px 12px 20px 12px;
          scroll-behavior: smooth;
        }
        .glass-sidebar-content.collapsed .sidebar-scroll { padding: 16px 6px 20px 6px; }
        .sidebar-scroll::-webkit-scrollbar { width: 3px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: var(--primary-blue);
          border-radius: 10px;
          opacity: 0.4;
        }

        /* --- Sections --- */
        .sidebar-section {
          margin-bottom: 8px;
          border-radius: var(--radius-md);
          transition: all 0.3s ease;
        }
        .sidebar-section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.3s var(--transition);
          user-select: none;
          background: transparent;
          border: none;
          width: 100%;
          text-align: left;
          color: var(--text-muted);
          font-family: 'Inter', sans-serif;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          position: relative;
        }
        .sidebar-section-header:hover {
          background: rgba(255,255,255,0.05);
          color: var(--text-light);
        }
        .sidebar-section-header.active {
          color: var(--accent-cyan);
        }
        .sidebar-section-header .section-icon {
          font-size: 0.9rem;
          width: 22px;
          opacity: 0.5;
          transition: all 0.3s ease;
        }
        .sidebar-section-header.active .section-icon {
          opacity: 1;
          color: var(--accent-cyan);
          filter: drop-shadow(0 0 6px var(--primary-glow));
        }
        .sidebar-section-header .section-label {
          flex: 1;
          font-weight: 700;
          letter-spacing: 1px;
        }
        .sidebar-section-header .section-chevron {
          font-size: 0.6rem;
          transition: transform 0.4s var(--transition), color 0.3s ease;
          opacity: 0.4;
        }
        .sidebar-section-header .section-chevron.open {
          transform: rotate(180deg);
          color: var(--accent-cyan);
          opacity: 1;
        }
        .sidebar-section-header .section-indicator {
          position: absolute;
          left: -12px;
          width: 3px;
          height: 22px;
          border-radius: 4px;
          background: transparent;
          transition: all 0.3s ease;
        }
        .sidebar-section-header.active .section-indicator {
          background: var(--accent-cyan);
          box-shadow: 0 0 24px var(--primary-glow);
        }
        .sidebar-section-content {
          overflow: hidden;
          transition: max-height 0.5s var(--transition), opacity 0.4s ease;
          max-height: 0;
          opacity: 0;
        }
        .sidebar-section-content.open {
          max-height: 800px;
          opacity: 1;
        }

        /* --- Liens --- */
        .nav-link-glass {
          position: relative;
          display: flex;
          align-items: center;
          padding: 10px 14px 10px 20px;
          margin-bottom: 3px;
          border-radius: var(--radius-md);
          color: var(--text-muted);
          text-decoration: none;
          transition: all 0.3s var(--transition);
          border: 1px solid transparent;
          white-space: nowrap;
          cursor: pointer;
          background: transparent;
          font-size: 0.9rem;
          font-weight: 500;
          gap: 14px;
          overflow: hidden;
        }
        .nav-link-glass:hover {
          background: rgba(59, 130, 246, 0.07);
          color: white;
          transform: translateX(4px);
          border-color: rgba(59, 130, 246, 0.06);
        }
        .nav-link-glass.active {
          background: rgba(59, 130, 246, 0.1);
          color: white;
          border-color: rgba(59, 130, 246, 0.12);
          box-shadow: inset 0 0 30px rgba(59, 130, 246, 0.02);
        }
        .nav-link-glass::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%) scaleY(0);
          width: 3px;
          height: 22px;
          border-radius: 4px;
          background: var(--accent-cyan);
          box-shadow: 0 0 20px var(--primary-glow);
          transition: transform 0.3s var(--transition);
        }
        .nav-link-glass.active::before {
          transform: translateY(-50%) scaleY(1);
        }
        .nav-link-glass .link-icon {
          width: 20px;
          display: flex;
          justify-content: center;
          font-size: 1rem;
          transition: all 0.3s var(--transition);
          flex-shrink: 0;
        }
        .nav-link-glass.active .link-icon {
          color: var(--accent-cyan);
          filter: drop-shadow(0 0 10px var(--primary-glow));
        }
        .nav-link-glass .link-text {
          flex: 1;
          font-size: 0.85rem;
        }
        .nav-link-glass .link-chevron {
          font-size: 0.55rem;
          opacity: 0;
          transform: translateX(-8px);
          transition: all 0.4s var(--transition);
          color: var(--accent-cyan);
        }
        .nav-link-glass.active .link-chevron {
          opacity: 1;
          transform: translateX(0);
        }

        /* --- Mode rétracté --- */
        .glass-sidebar-content.collapsed .nav-link-glass {
          padding: 0;
          margin: 0 auto 6px auto;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 16px;
          background: rgba(255,255,255,0.02);
          border: 1px solid transparent;
        }
        .glass-sidebar-content.collapsed .nav-link-glass:hover {
          transform: translateY(-3px) scale(1.05);
          background: rgba(59, 130, 246, 0.1);
        }
        .glass-sidebar-content.collapsed .nav-link-glass.active {
          background: rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.2);
          box-shadow: 0 0 30px var(--primary-glow);
        }
        .glass-sidebar-content.collapsed .nav-link-glass::before { display: none; }
        .glass-sidebar-content.collapsed .nav-link-glass .link-text,
        .glass-sidebar-content.collapsed .nav-link-glass .link-chevron {
          display: none;
        }
        .glass-sidebar-content.collapsed .nav-link-glass .link-icon {
          font-size: 1.2rem;
        }
        .glass-sidebar-content.collapsed .sidebar-section-header {
          padding: 8px 0;
          justify-content: center;
          margin-bottom: 2px;
        }
        .glass-sidebar-content.collapsed .sidebar-section-header .section-label,
        .glass-sidebar-content.collapsed .sidebar-section-header .section-chevron,
        .glass-sidebar-content.collapsed .sidebar-section-header .section-indicator {
          display: none;
        }
        .glass-sidebar-content.collapsed .sidebar-section-header .section-icon {
          font-size: 1rem;
          opacity: 0.4;
          width: auto;
        }
        .glass-sidebar-content.collapsed .sidebar-section-header:hover .section-icon {
          opacity: 1;
          color: var(--accent-cyan);
        }
        .glass-sidebar-content.collapsed .sidebar-section-content {
          max-height: 0 !important;
          opacity: 0 !important;
        }

        /* --- Tooltips (mode rétracté) --- */
        .glass-sidebar-content.collapsed .nav-link-glass[data-tooltip]:hover::before,
        .glass-sidebar-content.collapsed .nav-link-glass[data-tooltip]:hover::after {
          opacity: 1;
          visibility: visible;
          transform: translateY(-50%) scale(1);
        }
        .glass-sidebar-content.collapsed .nav-link-glass[data-tooltip]::before {
          content: attr(data-tooltip);
          position: absolute;
          left: calc(100% + 14px);
          top: 50%;
          transform: translateY(-50%) scale(0.8);
          padding: 8px 18px;
          border-radius: 12px;
          background: rgba(8, 15, 35, 0.95);
          backdrop-filter: blur(12px);
          color: white;
          font-size: 0.8rem;
          font-weight: 500;
          white-space: nowrap;
          border: 1px solid rgba(59, 130, 246, 0.15);
          box-shadow: 0 8px 30px rgba(0,0,0,0.5);
          z-index: 999;
          transition: all 0.25s var(--transition);
          opacity: 0;
          visibility: hidden;
          letter-spacing: 0.3px;
        }
        .glass-sidebar-content.collapsed .nav-link-glass[data-tooltip]::after {
          content: '';
          position: absolute;
          left: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%) scale(0);
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-right: 5px solid rgba(8, 15, 35, 0.95);
          transition: all 0.25s var(--transition);
          opacity: 0;
          visibility: hidden;
        }

        /* --- Footer --- */
        .sidebar-footer {
          flex-shrink: 0;
          padding: 14px 16px;
          margin-top: auto;
          border-top: 1px solid rgba(255,255,255,0.04);
          background: rgba(0, 10, 30, 0.25);
          backdrop-filter: blur(6px);
        }
        .glass-sidebar-content.collapsed .sidebar-footer {
          padding: 14px 4px;
          display: flex;
          justify-content: center;
        }
        .profile-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 8px 14px;
          border-radius: var(--radius-md);
          transition: 0.3s ease;
          background: rgba(255,255,255,0.02);
          border: 1px solid transparent;
          cursor: default;
        }
        .profile-card:hover {
          background: rgba(255,255,255,0.04);
          border-color: rgba(255,255,255,0.04);
        }
        .profile-avatar {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          background: linear-gradient(135deg, var(--primary-blue), var(--accent-cyan));
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.1rem;
          color: white;
          flex-shrink: 0;
          box-shadow: 0 4px 16px var(--primary-glow);
        }
        .profile-info {
          flex: 1;
          overflow: hidden;
        }
        .profile-info .name {
          font-size: 0.85rem;
          font-weight: 600;
          color: white;
        }
        .profile-info .role {
          font-size: 0.7rem;
          font-weight: 500;
          color: var(--text-muted);
          opacity: 0.8;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .profile-info .role .badge-role {
          background: rgba(59, 130, 246, 0.15);
          padding: 0 10px;
          border-radius: 30px;
          font-size: 0.6rem;
          font-weight: 600;
          color: var(--accent-cyan);
          border: 1px solid rgba(59, 130, 246, 0.1);
        }
        .glass-sidebar-content.collapsed .profile-card {
          padding: 4px;
          justify-content: center;
          border-radius: 50%;
        }
        .glass-sidebar-content.collapsed .profile-info {
          display: none;
        }
        .glass-sidebar-content.collapsed .profile-avatar {
          width: 38px;
          height: 38px;
          border-radius: 12px;
          font-size: 0.9rem;
        }

        /* --- Animation d'apparition --- */
        .glass-sidebar-content {
          animation: sidebarFadeIn 0.7s ease;
        }
        @keyframes sidebarFadeIn {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        /* --- Responsive --- */
        @media (max-width: 992px) {
          .glass-sidebar-content { width: 260px; border-radius: 0 24px 24px 0; }
          .glass-sidebar-content.collapsed { width: 72px; }
        }
        @media (max-width: 768px) {
          .glass-sidebar-content { width: 100%; border-radius: 0; }
          .glass-sidebar-content.collapsed { width: 72px; }
          .toggle-handle { display: none; }
        }
      `}</style>

      <div className={`glass-sidebar-content ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Bouton toggle */}
        <button className="toggle-handle" onClick={() => setIsCollapsed(!isCollapsed)}>
          <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
        </button>

        {/* Header */}
        <div className="sidebar-header">
          <div className="app-brand">
            <div className="brand-icon-wrapper">
              <FontAwesomeIcon icon={faLayerGroup} />
            </div>
            {!isCollapsed && (
              <div>
                <div className="brand-text">
                  BISEN<span className="brand-highlight">TER</span>
                </div>
                <div className="brand-sub">Enterprise Resource Planning</div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="sidebar-scroll">
          {sections.map((section) => {
            const isActive = isSectionActive(section.items);
            const isExpanded = expandedSections[section.id] !== false;

            return (
              <div key={section.id} className="sidebar-section">
                <button
                  className={`sidebar-section-header ${isActive ? 'active' : ''}`}
                  onClick={() => !isCollapsed && toggleSection(section.id)}
                  disabled={isCollapsed}
                >
                  <span className="section-indicator" />
                  <span className="section-icon">
                    <FontAwesomeIcon icon={section.icon} />
                  </span>
                  <span className="section-label">{section.label}</span>
                  <span className={`section-chevron ${isExpanded ? 'open' : ''}`}>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </span>
                </button>

                <div className={`sidebar-section-content ${isExpanded && !isCollapsed ? 'open' : ''}`}>
                  {section.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `nav-link-glass ${isActive ? 'active' : ''}`
                      }
                      onClick={handleLinkClick}
                      data-tooltip={item.label}
                    >
                      <span className="link-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </span>
                      <span className="link-text">{item.label}</span>
                      <span className="link-chevron">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer / Profil */}
        <div className="sidebar-footer">
          <div className="profile-card">
            <div className="profile-avatar">{userInitial}</div>
            {!isCollapsed && (
              <div className="profile-info">
                <div className="name">{fonction || 'Utilisateur'}</div>
                <div className="role">
                  Rôle
                  <span className="badge-role">{fonction || 'Standard'}</span>
                </div>
              </div>
            )}
            {!isCollapsed && (
              <FontAwesomeIcon icon={faUserGear} style={{ opacity: 0.3, fontSize: '0.9rem', color: 'var(--text-muted)' }} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;